import {
    BackendClient,
    type BackendClientParameters,
} from "@internal/backstage-plugin-utility-common";

/**
 * Parameters for creating any GitHub client or sub-client.
 *
 * @public
 */
export type GitHubClientOptions = Omit<
    BackendClientParameters,
    "targetPluginId"
>;

/**
 * Optional per-request options for GitHub client methods.
 *
 * @public
 */
export interface GitHubClientRequestOptions {
    /**
     * User's GitHub OAuth token. If provided, the backend will use this token
     * instead of the GitHub App installation token for this request.
     * Sent as the `x-github-token` header.
     */
    githubToken?: string;
}

/**
 * Options for the `getTree` method on {@link GitHubRepoClient}.
 *
 * @public
 */
export interface GitHubGetTreeOptions extends GitHubClientRequestOptions {
    /**
     * When true, fetches the git tree recursively.
     */
    recursive?: boolean;
}

/**
 * Base class for all GitHub client and sub-client implementations.
 *
 * Extends {@link BackendClient} with GitHub-specific configuration and the
 * `buildGitHubHeaders` helper used by all sub-clients to forward a user token.
 *
 * @public
 */
export class GitHubBaseClient extends BackendClient {
    constructor(options: GitHubClientOptions) {
        super({ ...options, targetPluginId: "github" });
    }

    /**
     * Builds request headers from per-request options.
     * Sets the `x-github-token` header when a token is provided.
     */
    protected buildGitHubHeaders(
        options?: GitHubClientRequestOptions
    ): Record<string, string> {
        const headers: Record<string, string> = {};
        if (options?.githubToken) {
            headers["x-github-token"] = options.githubToken;
        }
        return headers;
    }
}
